import type { RecipientOption } from "~/types/common";

export interface GroupedRecipientOption {
  /** 묶인 반려견 petSeq들을 ","로 연결한 값 (예: "12,34") — 제출 시 split 해서 각 펫 selected 처리 */
  value: string;
  /** "초코, 보리 (홍길동)" 형태 라벨 */
  label: string;
}

/** petName("초코 (홍길동)")에서 보호자 접미사를 제거해 순수 펫 이름만 얻는다 */
const rawPetName = (recipient: RecipientOption): string => {
  const { petName, userName } = recipient;
  if (userName && petName?.endsWith(` (${userName})`)) {
    return petName.slice(0, petName.length - userName.length - 3);
  }
  return petName || "이름 없음";
};

/**
 * 수신자 목록을 보호자(userSeq) 단위로 묶어 다중선택 옵션으로 변환.
 * 같은 보호자의 반려견들은 옵션 하나("초코, 보리 (홍길동)")로 묶이고,
 * 선택 시 value("12,34")를 split 해 묶인 펫 전체를 selected 처리한다.
 * userSeq가 없는 예외 데이터는 펫 단위로 개별 옵션이 된다.
 */
export const groupRecipientOptions = (
  recipients: RecipientOption[] | undefined | null
): GroupedRecipientOption[] => {
  const groups: Array<{
    petSeqs: number[];
    petNames: string[];
    userName: string | null;
  }> = [];
  const indexByKey = new Map<string, number>();

  for (const recipient of recipients ?? []) {
    const key =
      recipient.userSeq != null
        ? `u${recipient.userSeq}`
        : `p${recipient.petSeq}`;
    const idx = indexByKey.get(key);
    if (idx === undefined) {
      indexByKey.set(key, groups.length);
      groups.push({
        petSeqs: [recipient.petSeq],
        petNames: [rawPetName(recipient)],
        userName: recipient.userName ?? null,
      });
    } else {
      groups[idx].petSeqs.push(recipient.petSeq);
      groups[idx].petNames.push(rawPetName(recipient));
    }
  }

  return groups.map((group) => ({
    value: group.petSeqs.join(","),
    label: `${group.petNames.join(", ")}${
      group.userName ? ` (${group.userName})` : ""
    }`,
  }));
};

/** 선택된 옵션 value들("12,34" 형태 포함)을 개별 petSeq 문자열 집합으로 전개 */
export const expandRecipientValues = (
  value: string | string[] | undefined | null
): Set<string> => {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  return new Set(
    values.flatMap((v) => v.split(",")).filter((v) => v.length > 0)
  );
};
